/**
 * ================================================================================
 * CONTEXTO: FinanceContext
 * PROPÓSITO: Estado global da aplicação (useReducer + Context API)
 * Centraliza: user, wallets, notifications, settings, loading states
 * ================================================================================
 */
import React, { createContext, useContext, useReducer, useEffect, useCallback, useRef } from 'react';
import api from '../utils/api';

// ===== ESTADO INICIAL =====
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,

  // Dashboard
  dashboard: null,
  dashboardError: '',
  dashboardLoading: false,
  realtime: {
    connected: false,
    lastSyncAt: null,
    version: null,
  },

  // Notificações
  notifications: [],
  unreadCount: 0,

  // Settings
  settings: {},

  // Global
  sidebarOpen: false, // Mobile
};

// ===== ACTIONS =====
const ACTIONS = {
  SET_USER: 'SET_USER',
  LOGOUT: 'LOGOUT',
  SET_LOADING: 'SET_LOADING',
  SET_DASHBOARD: 'SET_DASHBOARD',
  SET_DASHBOARD_ERROR: 'SET_DASHBOARD_ERROR',
  SET_DASHBOARD_LOADING: 'SET_DASHBOARD_LOADING',
  SET_REALTIME_STATUS: 'SET_REALTIME_STATUS',
  SET_NOTIFICATIONS: 'SET_NOTIFICATIONS',
  MARK_NOTIFICATION_READ: 'MARK_NOTIFICATION_READ',
  SET_SETTINGS: 'SET_SETTINGS',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
};

// ===== REDUCER =====
function financeReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_USER:
      return { ...state, user: action.payload, isAuthenticated: true, loading: false };
    case ACTIONS.LOGOUT:
      return { ...initialState, loading: false };
    case ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    case ACTIONS.SET_DASHBOARD:
      return { ...state, dashboard: action.payload, dashboardError: '', dashboardLoading: false };
    case ACTIONS.SET_DASHBOARD_ERROR:
      return { ...state, dashboardError: action.payload, dashboardLoading: false };
    case ACTIONS.SET_DASHBOARD_LOADING:
      return { ...state, dashboardLoading: action.payload };
    case ACTIONS.SET_REALTIME_STATUS:
      return { ...state, realtime: { ...state.realtime, ...action.payload } };
    case ACTIONS.SET_NOTIFICATIONS:
      return { ...state, notifications: action.payload.notifications, unreadCount: action.payload.unread_count };
    case ACTIONS.MARK_NOTIFICATION_READ:
      return {
        ...state,
        notifications: state.notifications.map((n) =>
          n.id === action.payload ? { ...n, is_read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    case ACTIONS.SET_SETTINGS:
      return { ...state, settings: action.payload };
    case ACTIONS.TOGGLE_SIDEBAR:
      return { ...state, sidebarOpen: !state.sidebarOpen };
    default:
      return state;
  }
}

// ===== CONTEXT =====
const FinanceContext = createContext(null);

// ===== PROVIDER =====
export function FinanceProvider({ children }) {
  const [state, dispatch] = useReducer(financeReducer, initialState);
  const realtimeVersionRef = useRef(null);
  const dashboardParamsRef = useRef({ period: 'month' });
  const refreshInFlightRef = useRef(false);

  // Verificar token na inicialização
  useEffect(() => {
    localStorage.removeItem('kumbuos_token');
    api.get('/me')
      .then((res) => dispatch({ type: ACTIONS.SET_USER, payload: res.data }))
      .catch(() => dispatch({ type: ACTIONS.SET_LOADING, payload: false }));
  }, []);

  // REGISTRO
  const registerUser = useCallback(async (name, email, telegram_chat_id, password, password_confirmation) => {
    const res = await api.post('/register', { name, email, telegram_chat_id, password, password_confirmation });
    dispatch({ type: ACTIONS.SET_USER, payload: res.data.user });
    return res.data;
  }, []);

  // LOGIN
  const login = useCallback(async (email, password) => {
    const res = await api.post('/login', { email, password });
    dispatch({ type: ACTIONS.SET_USER, payload: res.data.user });
    return res.data;
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    try { await api.post('/logout'); } catch {}
    dispatch({ type: ACTIONS.LOGOUT });
  }, []);

  // FETCH DASHBOARD
  const fetchDashboard = useCallback(async (period = 'month', options = {}) => {
    const { silent = false, ...filters } = options;
    const params = { period, ...filters };
    dashboardParamsRef.current = params;
    if (!silent) {
      dispatch({ type: ACTIONS.SET_DASHBOARD_LOADING, payload: true });
    }
    try {
      const res = await api.get('/dashboard', { params });
      dispatch({ type: ACTIONS.SET_DASHBOARD, payload: res.data });
      return res.data;
    } catch (error) {
      if (!silent) {
        dispatch({ type: ACTIONS.SET_DASHBOARD_ERROR, payload: 'Nao foi possivel carregar o dashboard.' });
      }
      throw error;
    }
  }, []);

  // FETCH NOTIFICATIONS
  const fetchNotifications = useCallback(async () => {
    const res = await api.get('/notifications');
    dispatch({ type: ACTIONS.SET_NOTIFICATIONS, payload: res.data });
  }, []);

  // MARK NOTIFICATION READ
  const markNotificationRead = useCallback(async (id) => {
    await api.patch(`/notifications/${id}/read`);
    dispatch({ type: ACTIONS.MARK_NOTIFICATION_READ, payload: id });
  }, []);

  // FETCH SETTINGS
  const fetchSettings = useCallback(async () => {
    const res = await api.get('/settings');
    dispatch({ type: ACTIONS.SET_SETTINGS, payload: res.data });
  }, []);

  // TOGGLE SIDEBAR
  const toggleSidebar = useCallback(() => {
    dispatch({ type: ACTIONS.TOGGLE_SIDEBAR });
  }, []);

  useEffect(() => {
    if (!state.isAuthenticated) {
      realtimeVersionRef.current = null;
      dispatch({
        type: ACTIONS.SET_REALTIME_STATUS,
        payload: { connected: false, version: null, lastSyncAt: null },
      });
      return undefined;
    }

    let stopped = false;
    let timeoutId;

    const syncRealtime = async () => {
      if (stopped || refreshInFlightRef.current) return;

      refreshInFlightRef.current = true;
      try {
        const res = await api.get('/realtime/version');
        const nextVersion = res.data.version;
        const previousVersion = realtimeVersionRef.current;

        realtimeVersionRef.current = nextVersion;
        dispatch({
          type: ACTIONS.SET_REALTIME_STATUS,
          payload: {
            connected: true,
            version: nextVersion,
            lastSyncAt: res.data.server_time,
          },
        });

        if (previousVersion && previousVersion !== nextVersion) {
          await Promise.all([
            fetchDashboard(dashboardParamsRef.current.period, { ...dashboardParamsRef.current, silent: true }),
            fetchNotifications().catch(() => null),
          ]);
        }
      } catch {
        dispatch({ type: ACTIONS.SET_REALTIME_STATUS, payload: { connected: false } });
      } finally {
        refreshInFlightRef.current = false;
        if (!stopped) {
          const delay = document.visibilityState === 'visible' ? 60000 : 300000;
          timeoutId = window.setTimeout(syncRealtime, delay);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        window.clearTimeout(timeoutId);
        syncRealtime();
      }
    };

    syncRealtime();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      stopped = true;
      window.clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.isAuthenticated, fetchDashboard, fetchNotifications]);

  const value = {
    ...state,
    registerUser,
    login,
    logout,
    fetchDashboard,
    fetchNotifications,
    markNotificationRead,
    fetchSettings,
    toggleSidebar,
    dispatch,
    ACTIONS,
  };

  return (
    <FinanceContext.Provider value={value}>
      {children}
    </FinanceContext.Provider>
  );
}

// HOOK personalizado
export function useFinance() {
  const context = useContext(FinanceContext);
  if (!context) throw new Error('useFinance must be used within FinanceProvider');
  return context;
}

export default FinanceContext;
